import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPost, fetchPrivatePost } from 'src/modules/community/singlePost/actions/fetchActions';
import { showSecondPageBlockMessageBox } from 'src/modules/community/singlePost/actions/actions';
import { CommunityLayout } from 'src/layouts';
import SinglePostSidebar from 'src/modules/community/shared/components/sidebar/singlePostSidebar';
import PostContent from 'src/modules/community/singlePost/components/postContent';
import { useTopConditionModal } from 'src/modules/profileSurvey/components/communityTopConditionsModal';
import { useIsCommunityMember } from 'src/utils/context';
import { trackFootfallEvent } from 'src/utils/footfall';
import { useUpdateEffect } from 'src/utils/hooks';
import { getPostSeo } from 'src/utils/seo';
import EventType from 'src/constants/footfallEventTypes';
import { State } from 'src/types';
import dynamic from 'next/dynamic';
const AdvertModalContainer = dynamic(() => import('src/components/advertModalContainer'), { ssr: false });

export type singlePostPageProps = {
  scrollToResponseId: string,
};

export default function SinglePostPage({ scrollToResponseId }: singlePostPageProps) {
  const {
    relatedPosts,
    post,
    nextPosts,
  } = useSelector(({pages}: State) => pages.singlePost);
  const { currentCommunity, currentUser } = useSelector(({context}: State) => context);

  useFetchPost();
  usePostFootfall();
  useSecondPageBlock();
  useTopConditionModal();

  // TODO: move this into relevant components
  let relatedPostsByLoggedInStatus = relatedPosts && relatedPosts.slice(5, 11);
  let nextPostsOrRelated = relatedPosts;
  if (!!currentUser && nextPosts.length > 0) {
    nextPostsOrRelated = nextPosts;
    relatedPostsByLoggedInStatus = relatedPosts;
  }

  if (!post || !currentCommunity) return null;

  return (
    <CommunityLayout
      currentPage="post"
      seo={post && currentCommunity ? getPostSeo(post, currentCommunity) : {}}
      sideBarContent={<SinglePostSidebar relatedPosts={relatedPostsByLoggedInStatus} />}
    >
      <PostContent
        nextPosts={nextPostsOrRelated}
        scrollToResponseId={scrollToResponseId}
      />
      <AdvertModalContainer currentPage="post" />
    </CommunityLayout>
  );
}

const usePostFootfall = () => {
  const postId = useSelector(({pages}: State) => pages.singlePost.post?.id);
  useEffect(() => {
    if (!postId) return;
    trackFootfallEvent(EventType.VIEWED_POST, { postId });
  }, [postId]);
};

const useFetchPost = () => {
  const dispatch = useDispatch();
  const id = useSelector(({ pages }: State) => pages.singlePost.post?.id);
  const slug = useSelector(({ context }: State) => context.currentCommunity?.slug);
  const followedCommunities = useSelector(({ context }: State) => context.currentUser?.followedCommunities);
  const isCommunityMember = useIsCommunityMember();

  useUpdateEffect(() => {
    if (!id || !slug) return;
    if (isCommunityMember) {
      dispatch(fetchPrivatePost(id, slug));
    } else {
      dispatch(fetchPost(id, slug));
    }
  }, [followedCommunities]);
};

export const useIsFreePost = (): boolean => {
  const postId = useSelector(({pages}: State) => pages.singlePost.post?.id);
  const freePostId = useSelector(({pages}: State) => pages.singlePost.freePostId);
  return postId === freePostId;
};

const useSecondPageBlock = () => {
  const dispatch = useDispatch();
  const isFreePost = useIsFreePost();
  const { currentUser } = useSelector(({context}: State) => context);

  useEffect(() => {
    if (!isFreePost && !currentUser) {
      trackFootfallEvent(EventType.CLICKED, { clickType: 'sign-up', clickMetadata: { section: 'second-post' } });
      dispatch(showSecondPageBlockMessageBox());
    }
  }, [currentUser, dispatch, isFreePost]);
};
